import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { Link } from "@/i18n/navigation";
import { Tag } from "@repo/ui/tag";
import { cn } from "@repo/ui/utils";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { isExternalUrl } from "@/lib/utils";
import type { VisualType } from "@/lib/types";

/** Breakpoints kept in sync with Tailwind config (spacing.css custom variants) */
const BREAKPOINTS = {
  desktop: 1024,
  // 64rem — matches desktop: custom variant
  desktopLg: 1280 // 80rem — matches desktop-lg: custom variant
} as const;
export const DEFAULT_CARD_IMAGE_SIZES = `(max-width: ${BREAKPOINTS.desktop}px) 100vw, (max-width: ${BREAKPOINTS.desktopLg}px) 50vw, 33vw`;
export const DEFAULT_CARD_CROP_ASPECT = 16 / 9;
export function ListingCardShell(t0) {
  const $ = _c(14);
  const {
    href,
    gapClass,
    className,
    children
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== gapClass) {
    t1 = cn("group flex flex-col items-start", gapClass, className);
    $[0] = className;
    $[1] = gapClass;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rootClass = t1;
  if (!href) {
    let t2;
    if ($[3] !== children || $[4] !== rootClass) {
      t2 = <div className={rootClass}>{children}</div>;
      $[3] = children;
      $[4] = rootClass;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  if (isExternalUrl(href)) {
    let t2;
    if ($[6] !== children || $[7] !== href || $[8] !== rootClass) {
      t2 = <a className={rootClass} href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
      $[6] = children;
      $[7] = href;
      $[8] = rootClass;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t2;
  if ($[10] !== children || $[11] !== href || $[12] !== rootClass) {
    t2 = <Link className={rootClass} href={href}>{children}</Link>;
    $[10] = children;
    $[11] = href;
    $[12] = rootClass;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}
export function ListingCardImage(t0) {
  const $ = _c(11);
  const {
    visual,
    overlay,
    sizes: t1,
    cropAspect: t2,
    isLogo: t3
  } = t0;
  const sizes = t1 === undefined ? DEFAULT_CARD_IMAGE_SIZES : t1;
  const cropAspect = t2 === undefined ? DEFAULT_CARD_CROP_ASPECT : t2;
  const isLogo = t3 === undefined ? false : t3;
  const t4 = isLogo && "bg-surface-base-secondary";
  let t5;
  if ($[0] !== t4) {
    t5 = cn("relative aspect-[358/245] min-h-0 w-full shrink-0 overflow-clip rounded-sm desktop:aspect-[421/289] desktop:rounded-md", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== cropAspect || $[3] !== isLogo || $[4] !== sizes || $[5] !== visual) {
    t6 = visual && <VisualWrapper visual={visual} className="size-full" visualClassName="size-full" imgClassName={isLogo ? undefined : "object-cover transition-transform duration-300 ease-out group-hover:scale-105"} extraImageProps={{
      sizes,
      visualOrientation: "0",
      ...(isLogo ? {
        resizeMode: "contain"
      } : {
        cropAspect
      })
    }} />;
    $[2] = cropAspect;
    $[3] = isLogo;
    $[4] = sizes;
    $[5] = visual;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== overlay || $[8] !== t5 || $[9] !== t6) {
    t7 = <div className={t5}>{t6}{overlay}</div>;
    $[7] = overlay;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
export function ListingCardContent(t0) {
  const $ = _c(5);
  const {
    gapClass,
    children
  } = t0;
  let t1;
  if ($[0] !== gapClass) {
    t1 = cn("flex w-full flex-col items-start", gapClass);
    $[0] = gapClass;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/** Category Tag pills overlaid bottom-left on the card image. */
export function CategoryTags(t0) {
  const $ = _c(9);
  const {
    categories,
    max
  } = t0;
  let t1;
  if ($[0] !== categories) {
    t1 = new Set(categories);
    $[0] = categories;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = [...t1];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const unique = t2;
  if (unique.length === 0) {
    return null;
  }
  let t3;
  if ($[4] !== max || $[5] !== unique) {
    t3 = max == null ? unique : unique.slice(0, max);
    $[4] = max;
    $[5] = unique;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const shown = t3;
  let t4;
  if ($[7] !== shown) {
    t4 = <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">{shown.map(_temp)}</div>;
    $[7] = shown;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp(tag) {
  return <Tag variant="transparent-dark" size="sm" key={tag}>{tag}</Tag>;
}